import { useMemo } from 'react';
import { useLocations, useDepartments } from '@/hooks/useApi';

export interface LocationDepartment {
  id: string;
  name: string;
  employee_count: number;
}

interface LocationShape {
  id: string;
  organization_id?: string | null;
}

interface DepartmentShape {
  id: string;
  name: string;
  organization_id?: string | null;
  _count?: { employees?: number } | null;
}

/**
 * Возвращает подразделения организации выбранной локации.
 *
 * Резолюция: locationId → locations API → organization_id → departments API.
 * employee_count берётся из бэкенда _count.employees (если есть), иначе 0.
 *
 * Если у локации нет organization_id (редко), возвращает пустой массив.
 */
export function useLocationDepartments(locationId: string | null) {
  const locationsQuery = useLocations({ limit: 200 });

  const organizationId = useMemo<string | null>(() => {
    if (!locationId) return null;
    const list = (locationsQuery.data as { data?: LocationShape[] })?.data;
    if (!list) return null;
    const loc = list.find((l) => l.id === locationId);
    return loc?.organization_id ?? null;
  }, [locationId, locationsQuery.data]);

  const departmentsQuery = useDepartments(
    organizationId ? { organization_id: organizationId, limit: 200 } : {},
    { enabled: !!organizationId },
  );

  const departments = useMemo<LocationDepartment[]>(() => {
    if (!organizationId) return [];
    const list = (departmentsQuery.data as { data?: DepartmentShape[] })?.data;
    if (!list) return [];
    return list
      .map((d) => ({
        id: d.id,
        name: d.name,
        employee_count: d._count?.employees ?? 0,
      }))
      .sort((a, b) => a.name.localeCompare(b.name, 'ru'));
  }, [organizationId, departmentsQuery.data]);

  return {
    departments,
    isLoading: locationsQuery.isLoading || departmentsQuery.isLoading,
    isError: locationsQuery.isError || departmentsQuery.isError,
  };
}
